import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ShoppingCart, ArrowRight, Check, ChevronDown, Compass, Calendar, Store } from 'lucide-react';
import { haptic } from '@/utils/haptics';

/**
 * Mobile-optimized Landing Page for Logged-Out Users
 * Goal: "This helps me get the week done, not just pick recipes."
 * Mental flow: 1) Does the week for me → 2) Not a meal kit → 3) Don't have to think → 4) Can change later → 5) Tap CTA
 */
const MobileLanding = () => {
  const navigate = useNavigate();
  const [showPricing, setShowPricing] = useState(false);

  const handleGetStarted = () => {
    haptic('medium');
    navigate('/auth?signup=true');
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0]" data-testid="mobile-landing">
      {/* ==================== ABOVE THE FOLD ==================== */}
      <div className="min-h-[90vh] flex flex-col px-6 pt-8 pb-4">
        {/* Logo */}
        <div className="flex items-center gap-2 mb-10">
          <ShoppingCart className="h-7 w-7 text-[#4A5D4A]" />
          <span className="text-xl font-semibold text-[#2D3A2D]">Carted</span>
        </div>

        {/* Hero Content */}
        <div className="flex-1 flex flex-col justify-center">
          {/* Headline - Outcome-focused */}
          <h1 className="text-[2.25rem] font-bold tracking-tight leading-[1.1] text-[#2D3A2D] mb-3">
            Dinner, planned.<br />
            Groceries, handled.
          </h1>

          {/* Subheadline - Slightly smaller for mobile readability */}
          <p className="text-[15px] text-[#4A5A4A] leading-relaxed mb-2">
            Choose how you want the week to feel. We&apos;ll build the meals and the grocery cart.
          </p>

          {/* Meal-kit objection killer */}
          <p className="text-sm text-[#4A5A4A] mb-5">
            No subscriptions. No meal kits. Just groceries you trust.
          </p>

          {/* Checklist - Tighter spacing */}
          <div className="space-y-2.5 mb-5" data-testid="what-happens-next">
            {[
              'Pick a weekly meal style (healthy, fast, family)',
              'Get a flexible dinner plan automatically',
              'Build a complete grocery cart in one tap'
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-2.5 text-sm text-[#3D4D3D]">
                <Check className="h-4 w-4 text-[#4A5D4A] flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mb-3">
            <Button 
              size="lg" 
              className="w-full h-14 text-base rounded-full bg-[#4A5D4A] hover:bg-[#3D4D3D] active:scale-[0.98] text-white"
              onClick={handleGetStarted}
              data-testid="get-started-btn"
            >
              Plan My Week
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            {/* Trust signal + Flexibility reassurance */}
            <p className="text-center text-xs text-[#4A5A4A] mt-2">
              Free to start · No credit card
            </p>
            <p className="text-center text-xs text-[#5A6A5A] mt-1 italic">
              Swap meals, ingredients, or skip weeks anytime.
            </p>
          </div>

          {/* Login Link */}
          <p className="text-center text-sm text-[#5A6A5A] mb-4">
            Already have an account?{' '}
            <Link 
              to="/auth" 
              className="text-[#2D3A2D] font-medium underline"
              data-testid="signin-link"
            >
              Sign in
            </Link>
          </p>
        </div>

        {/* Simplified Icon Row - Shorter labels for mobile */}
        <div className="grid grid-cols-3 gap-3 py-5 border-t border-[#D5D5C0]" data-testid="how-it-works-mini">
          {[
            { icon: Compass, label: 'Choose a path', sublabel: 'How you want the week to feel' },
            { icon: Calendar, label: 'Weekly plan', sublabel: '5 flexible dinners' },
            { icon: Store, label: 'One cart', sublabel: 'Ready to checkout' },
          ].map((step, index) => (
            <div key={index} className="text-center">
              <div className="flex justify-center mb-1.5">
                <step.icon className="h-5 w-5 text-[#4A5D4A]" />
              </div>
              <p className="text-xs font-semibold text-[#2D3A2D] leading-tight">{step.label}</p>
              <p className="text-[10px] text-[#5A6A5A] mt-0.5 leading-tight">{step.sublabel}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ==================== PRO SECTION - Pushed Lower ==================== */}
      <div className="px-6 pb-8 bg-[#F5F5F0]">
        {/* Collapsed Pro teaser - Less prominent */}
        <button
          onClick={() => {
            haptic('light');
            setShowPricing(!showPricing);
          }}
          className="w-full py-3 text-center text-xs text-[#6A7A6A]"
          data-testid="view-pro-benefits"
        >
          <span className="inline-flex items-center">
            View Pro details
            <ChevronDown className={`h-3 w-3 ml-1 transition-transform ${showPricing ? 'rotate-180' : ''}`} />
          </span>
        </button>

        {/* Expanded Pricing */}
        {showPricing && (
          <div className="space-y-4 mt-4 animate-in slide-in-from-top-4" data-testid="pricing-expanded">
            {/* Pro Monthly */}
            <div className="bg-white rounded-2xl p-5 border border-[#D5D5C0]">
              <div className="flex items-center gap-2 mb-3">
                <span className="text-lg">⭐</span>
                <span className="font-semibold text-[#2D3A2D]">Pro</span>
                <span className="text-[#5A6A5A]">— $7 / month</span>
              </div>
              <ul className="space-y-2 text-sm text-[#5A6A5A] mb-4">
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-[#4A5D4A]" />
                  Unlimited meal planning
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-[#4A5D4A]" />
                  Unlimited grocery lists
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-[#4A5D4A]" />
                  One-tap store exports
                </li>
              </ul>
              <Link to="/auth?plan=pro">
                <Button 
                  variant="outline" 
                  className="w-full h-12 rounded-full border-[#4A5D4A] text-[#4A5D4A]"
                  data-testid="start-pro-btn"
                >
                  Start Pro
                </Button>
              </Link>
            </div>

            {/* Pro Yearly - Best Value */}
            <div className="bg-[#4A5D4A]/5 rounded-2xl p-5 border-2 border-[#4A5D4A]">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-lg">🟢</span>
                <span className="font-semibold text-[#2D3A2D]">Pro Yearly</span>
                <span className="text-[#5A6A5A]">— $60 / year</span>
              </div>
              <p className="text-xs text-[#4A5D4A] font-medium mb-3">
                Best value — save 29%
              </p>
              <Link to="/auth?plan=pro-yearly">
                <Button 
                  className="w-full h-12 rounded-full bg-[#4A5D4A] hover:bg-[#3D4D3D] text-white"
                  data-testid="start-pro-yearly-btn"
                >
                  Start Pro Yearly
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Bottom safe area */}
      <div className="h-8" />
    </div>
  );
};

export default MobileLanding;
